import React, { useCallback, useMemo } from "react";
import {
  FilterBar,
  FilterDateRange,
  FilterMultiSelect,
  FilterSingleSelect,
  FilterTimeRange,
  Icon,
  Text,
  Tooltip,
} from "components/commons";
import {
  locationWithTransactionFilterResponse,
  locationWithTransactionFilterRequest,
  staffWithTransactionFilterRequest,
  staffWithTransactionFilterResponse,
} from "mappers";
import { searchLocationForTransactionFilter, searchStaffWithTransaction } from "apis";
import {
  ExternalPaymentStatus,
  TapStatus,
  TransactionStatus,
  TransactionType,
} from "enums";
import {
  prettifyTapStatus,
  prettifyTransactionStatus,
  prettifyTransactionType,
} from "services";
import { mapObject, mapObjectsToSelect } from "services";
import lang from "translations";
import { useApi, useMount } from "hooks";
import { prettifyExPaymentStatus } from "services/pretty.service";

const TransactionFilter = ({
  filterState,
  requestState,
  modifyFilter,
  applyFilter,
  clearFilter,
}) => {
  const {
    request: searchLocationRequest,
    loading: loadingLocation,
    mappedData: locations,
  } = useApi({
    api: searchLocationForTransactionFilter,
    isArray: true,
    mapper: locationWithTransactionFilterResponse,
  });

  const {
    request: searchStaffRequest,
    loading: loadingStaff,
    mappedData: staffs,
  } = useApi({
    api: searchStaffWithTransaction,
    isArray: true,
    mapper: staffWithTransactionFilterResponse,
  });

  useMount(() => {
    fetchLocationRequest(requestState);
    fetchStaffRequest(requestState);
  });

  const fetchLocationRequest = useCallback(
    (requestState) => {
      searchLocationRequest(mapObject({ ...requestState }, locationWithTransactionFilterRequest));
    },
    [searchLocationRequest]
  );

  const fetchStaffRequest = useCallback(
    (requestState) => {
      searchStaffRequest(mapObject({ ...requestState }, staffWithTransactionFilterRequest));
    },
    [searchStaffRequest]
  );

  const applyFilterCb = useCallback(
    async (searchKey) => {
      await applyFilter(searchKey);
    },
    [applyFilter]
  );

  const clearFilterCb = useCallback(() => {
    clearFilter();
  }, [clearFilter]);

  const changeDateRangeCb = useCallback(
    (name, value) => {
      const { requestState } = modifyFilter(name, { value });
      fetchLocationRequest(requestState);
      fetchStaffRequest(requestState);
    },
    [fetchLocationRequest, fetchStaffRequest, modifyFilter]
  );

  const locationOptions = useMemo(() => {
    return mapObjectsToSelect(locations, { textKey: "name" });
  }, [locations]);

  const staffOptions = useMemo(() => {
    return mapObjectsToSelect(staffs, { textKey: "name" });
  }, [staffs]);

  const transactionTypeOptions = useMemo(() => {
    const transactionType = [
      TransactionType.SALE,
      TransactionType.TOPUP,
      TransactionType.RETURN,
      TransactionType.REDEEM,
      TransactionType.MIXED,
      TransactionType.ISSUE_FOC,
      TransactionType.REMOVE_FOC,
      TransactionType.VOUCHER_ISSUE,
      TransactionType.VOUCHER_REMOVE,
    ];
    const t = transactionType.map((type) => {
      return { text: prettifyTransactionType(type), value: type };
    });
    return t;
  }, []);

  const transactionStatusOptions = useMemo(() => {
    const transactionStatus = [
      TransactionStatus.SUCCESS,
      TransactionStatus.VOID,
      TransactionStatus.EDITED,
    ];
    const prettyTransactionStatus = transactionStatus.map((type) => {
      return { text: prettifyTransactionStatus(type), value: type };
    });
    return [{ text: lang.allStatus, value: null }, ...prettyTransactionStatus];
  }, []);

  const tapStatusOptions = useMemo(() => {
    const tapStatus = [TapStatus.Success, TapStatus.Failed];
    const prettyTapStatus = tapStatus.map((status) => {
      return { text: prettifyTapStatus(status), value: status };
    });
    return [{ text: lang.allStatus, value: null }, ...prettyTapStatus];
  }, []);

  const exPaymentStatusOptions = useMemo(() => {
    const statusArr = [
      ExternalPaymentStatus.Success,
      ExternalPaymentStatus.Failed,
      ExternalPaymentStatus.Pending,
      ExternalPaymentStatus.Timeout,
    ];
    const prettyStatus = statusArr.map((status) => ({
      text: prettifyExPaymentStatus(status),
      value: status,
    }));
    return [...prettyStatus, { text: lang.na, value: "NONE" }];
  }, []);

  return (
    <FilterBar
      placeholder={lang.searchTransactionPlaceholder}
      className="mb-lg"
      onClear={clearFilterCb}
      onApply={applyFilterCb}
      filterState={filterState}
    >
      <FilterDateRange
        id="dateRange"
        label={lang.dates}
        value={filterState.dateRange}
        maxDays={90}
        onChange={(value) => changeDateRangeCb("dateRange", value)}
      />
      <FilterTimeRange
        id="timeRange"
        label={lang.time}
        value={filterState.dateRange}
        onChange={(value) => modifyFilter("dateRange", { value })}
      />
      <FilterMultiSelect
        id="locations"
        label={lang.outlet}
        allLabel={lang.allLocations}
        loading={loadingLocation}
        options={locationOptions}
        value={filterState.locations}
        onChange={(value) => modifyFilter("locations", { value })}
      />
      <FilterMultiSelect
        id="staffs"
        label={lang.staff}
        allLabel={lang.allStaff}
        loading={loadingStaff}
        options={staffOptions}
        value={filterState.staffs}
        onChange={(value) => modifyFilter("staffs", { value })}
      />
      <FilterMultiSelect
        id="transactionTypes"
        label={lang.transactionType}
        allLabel={lang.allTransactions}
        options={transactionTypeOptions}
        value={filterState.transactionTypes}
        onChange={(value) => modifyFilter("transactionTypes", { value })}
      />
      <FilterSingleSelect
        id="transactionStatus"
        label={lang.transactionStatus}
        summaryFallback={lang.allStatus}
        options={transactionStatusOptions}
        value={filterState.transactionStatus}
        onChange={(value) => modifyFilter("transactionStatus", { value })}
      />
      <FilterSingleSelect
        id="tapStatus"
        label={lang.nfcStatus}
        summaryFallback={lang.allStatus}
        options={tapStatusOptions}
        value={filterState.tapStatus}
        onChange={(value) => modifyFilter("tapStatus", { value })}
      />
      <FilterMultiSelect
        id="externalPaymentStatus"
        align="right"
        label={
          <span className="flex items-center">
            {lang.externalPayment}
            <Tooltip
              title={
                <Text className="text-center" color="text-white">
                  {lang.thisIsForDirectIntegrationWithPaymentTerminalPartner}
                </Text>
              }
              placement="top"
            >
              <span className="ml-xs flex">
                <Icon name="info" fontSize="14px" color="text-gray-light" />
              </span>
            </Tooltip>
          </span>
        }
        allLabel={lang.all}
        options={exPaymentStatusOptions}
        value={filterState.externalPaymentStatus}
        onChange={(value) => modifyFilter("externalPaymentStatus", { value })}
      />
    </FilterBar>
  );
};

export default TransactionFilter;
